import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTitle } from '../../context/TitleContext';
import DataTable from '../../components/DataTable/DataTable';
import categoryRepository from '../../services/repositories/category.repository';
import { buildImageUrl } from '../../utils/imageUrl';
import '../../styles/products.scss';

const CategoryIndex = () => {
  const { setTitle } = useTitle();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryRepository.getAll();
      
      const transformedCategories = Array.isArray(response) ? response : (response.data || []);
      
      const formattedCategories = transformedCategories.map(category => {
        const imageUrl = buildImageUrl(category.image);
        
        return {
          id: category.id,
          name: category.name,
          description: category.description || '-',
          image: imageUrl ? [imageUrl] : [],
          disabled_fes: Boolean(category.disabled_fes),
          disabled_casa: Boolean(category.disabled_casa),
          in_store_only: Boolean(category.in_store_only),
        };
      });
      
      setCategories(formattedCategories);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err.message || 'Error loading categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const columns = useMemo(() => [
    {
      header: 'Image',
      accessor: 'image',
      type: 'image',
      sortable: false,
    },
    {
      header: 'Name',
      accessor: 'name',
      sortable: true,
    },
    {
      header: 'Description',
      accessor: 'description',
      sortable: true,
    },
    {
      header: 'Fès',
      accessor: 'disabled_fes',
      sortable: true,
      render: (value) => (value ? 'Désactivé' : 'Actif'),
    },
    {
      header: 'Casa',
      accessor: 'disabled_casa',
      sortable: true,
      render: (value) => (value ? 'Désactivé' : 'Actif'),
    },
    {
      header: 'Sur place',
      accessor: 'in_store_only',
      sortable: true,
      render: (value) => (value ? 'Oui' : 'Non'),
    },
  ], []);

  const handleAdd = useCallback(() => {
    navigate('/admin/categories/category-add');
  }, [navigate]);

  const handleEdit = useCallback((category) => {
    navigate(`/admin/categories/category-edit/${category.id}`);
  }, [navigate]);

  const handleDelete = useCallback(async (category) => {
    if (window.confirm(`Are you sure you want to delete "${category.name}"?`)) {
      try {
        setLoading(true);
        await categoryRepository.delete(category.id);
        alert('Category deleted successfully');
        await fetchCategories();
      } catch (error) {
        console.error('Delete error:', error);
        alert('Error deleting category');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchCategories]);

  const handleBulkDelete = useCallback(async (selectedIds) => {
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} categories?`)) {
      try {
        setLoading(true);
        for (const id of selectedIds) {
          await categoryRepository.delete(id);
        }
        alert(`${selectedIds.length} categories deleted successfully`);
        await fetchCategories();
      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('Error deleting categories');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchCategories]);

  useEffect(() => {
    setTitle('Categories');
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="categories-page">
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      <DataTable
        data={categories}
        columns={columns}
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onBulkAction={handleBulkDelete}
        title="Categories List"
        addButtonText="Add New Category"
        searchPlaceholder="Search categories..."
        pageSize={10}
        selectable={true}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default CategoryIndex;
